import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Save, RefreshCw, Layers, Store, Users, QrCode } from "lucide-react";
import { apiBoss } from "@/lib/api";

export default function BossStatsPage() {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    custom_cafes: "",
    custom_customers: "",
    custom_menus: "",
    custom_tables: "",
  });

  const loadStats = () => {
    setLoading(true);
    apiBoss.get("/boss/stats")
      .then(({ data }) => {
        setStatsData(data);
        setForm({
          custom_cafes: data.overrides.custom_cafes ?? "",
          custom_customers: data.overrides.custom_customers ?? "",
          custom_menus: data.overrides.custom_menus ?? "",
          custom_tables: data.overrides.custom_tables ?? "",
        });
      })
      .catch(() => toast.error("Failed to load statistics configuration"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        custom_cafes: form.custom_cafes === "" ? null : parseInt(form.custom_cafes),
        custom_customers: form.custom_customers === "" ? null : parseInt(form.custom_customers),
        custom_menus: form.custom_menus === "" ? null : parseInt(form.custom_menus),
        custom_tables: form.custom_tables === "" ? null : parseInt(form.custom_tables),
      };
      await apiBoss.put("/boss/stats", payload);
      toast.success("Platform statistics configuration saved successfully!");
      loadStats();
    } catch (err) {
      toast.error("Failed to save statistics overrides.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetOverrides = async () => {
    if (!window.confirm("Reset overrides and revert homepage statistics to real database counts?")) return;
    try {
      await apiBoss.put("/boss/stats", {
        custom_cafes: null,
        custom_customers: null,
        custom_menus: null,
        custom_tables: null,
      });
      toast.success("Overrides cleared. Homepage will now display exact database counts.");
      loadStats();
    } catch (err) {
      toast.error("Failed to reset overrides.");
    }
  };

  if (loading || !statsData) {
    return <div className="py-12 text-center text-slate-500 font-mono">Loading Stats Configuration...</div>;
  }

  const { real } = statsData;

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      <div className="border-b border-slate-800 pb-6">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Analytics Control</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Website Statistics Management</h1>
        <p className="text-slate-400 text-xs mt-1">
          Homepage metrics can either auto-calculate from real database records or be customized by the Boss.
        </p>
      </div>

      {/* Real-time DB counts card grid */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-bold text-white">Actual Real-Time Database Counts</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono">
          <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl space-y-1">
            <div className="text-slate-400 flex items-center gap-2">
              <Store className="w-4 h-4 text-amber-400" /> Active Cafes
            </div>
            <div className="font-heading text-2xl font-bold text-white">{real.cafes}</div>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl space-y-1">
            <div className="text-slate-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" /> Connected Customers
            </div>
            <div className="font-heading text-2xl font-bold text-white">{real.customers}</div>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl space-y-1">
            <div className="text-slate-400 flex items-center gap-2">
              <QrCode className="w-4 h-4 text-emerald-400" /> Active Menus
            </div>
            <div className="font-heading text-2xl font-bold text-white">{real.menus}</div>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl space-y-1">
            <div className="text-slate-400 flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-400" /> Total Tables
            </div>
            <div className="font-heading text-2xl font-bold text-white">{real.tables}</div>
          </div>
        </div>
      </div>

      {/* Custom Overrides Form */}
      <form onSubmit={handleSave} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div>
            <h3 className="font-heading text-xl font-bold text-white">Homepage Display Overrides</h3>
            <p className="text-xs text-slate-400">Leave blank to use live auto-calculated values.</p>
          </div>

          <button
            type="button"
            onClick={handleResetOverrides}
            className="text-xs font-mono text-slate-400 hover:text-white flex items-center gap-1.5 underline"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset To Real DB Counts
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs">
          <div>
            <label className="block font-mono uppercase text-slate-400 mb-2">
              Cafes & Restaurants Completed (Display Number)
            </label>
            <input
              type="number"
              placeholder={`Auto: ${real.cafes}`}
              value={form.custom_cafes}
              onChange={(e) => setForm({ ...form, custom_cafes: e.target.value })}
              className="w-full h-12 px-4 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
            />
          </div>

          <div>
            <label className="block font-mono uppercase text-slate-400 mb-2">
              Customers Connected (Display Number)
            </label>
            <input
              type="number"
              placeholder={`Auto: ${real.customers}`}
              value={form.custom_customers}
              onChange={(e) => setForm({ ...form, custom_customers: e.target.value })}
              className="w-full h-12 px-4 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
            />
          </div>

          <div>
            <label className="block font-mono uppercase text-slate-400 mb-2">
              QR Menus Created (Display Number)
            </label>
            <input
              type="number"
              placeholder={`Auto: ${real.menus}`}
              value={form.custom_menus}
              onChange={(e) => setForm({ ...form, custom_menus: e.target.value })}
              className="w-full h-12 px-4 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
            />
          </div>

          <div>
            <label className="block font-mono uppercase text-slate-400 mb-2">
              Tables Connected (Display Number)
            </label>
            <input
              type="number"
              placeholder={`Auto: ${real.tables}`}
              value={form.custom_tables}
              onChange={(e) => setForm({ ...form, custom_tables: e.target.value })}
              className="w-full h-12 px-4 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm"
            />
          </div>
        </div>

        <div className="pt-4 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="h-12 px-8 rounded-full bg-primary text-white font-semibold flex items-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? "Saving Configuration..." : "Save Statistics Settings"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
